import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Knack pack + subscribe-and-save selector (PDP centrepiece). ORIGINAL Horizon code.
 * Reference analysis: specs/fittr-pdp-analysis.md. Tracking: specs/fittr-tracking-upstack.md.
 *
 * Pack cards are the Subscribe & Save offer (a radio group); the native add-to-cart adds the
 * selected pack on subscription. On change the selector STEERS Horizon's native product form
 * (input[name="id"] + selling_plan) so the native add-to-cart AND native sticky bar add the
 * selection; mirrors the price into the sticky bar; publishes knack:pack:change; and mirrors
 * FITTR's Upstack view_content (charged value) when present.
 *
 * Deliberately does NOT send a custom Upstack add_to_cart: the Upstack app embed's own automatic
 * event detection already reports add-to-cart independently of any theme code, so a custom call
 * here double-counts every add. Confirmed live by suppressing the custom call entirely and still
 * seeing Upstack report an add_to_cart from its own detection alone. See DECISIONS.md.
 *
 * The "Buy once" control (archetype A) is a one-click button, NOT a radio: clicking it adds the
 * currently selected pack's variant as a one-time line (no selling plan) straight to the cart and
 * opens the drawer, mirroring Horizon's native add (requests cart sections, dispatches CartAddEvent).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [status]
 * @extends {Component<Refs>}
 */
export class KnackPackSelector extends Component {
  #scopeEl = null;
  #prefetched = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#scopeEl = this.closest('.shopify-section') ?? document;
    this.#relocateOnetimeExtras();
    this.#setupFlavourPrefetch();
    this.#scopeEl.addEventListener('change', this.#onChange);
    this.#scopeEl.addEventListener('click', this.#onBuyOnceClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#update('init');
  }

  /**
   * Prefetch a flavour's page on first hover / focus / touch so navigating between flavours feels
   * near-instant (each flavour stays a real page for SEO + analytics; this just warms the cache).
   */
  #setupFlavourPrefetch() {
    const scope = this.#scopeEl ?? document;
    const prefetch = (event) => {
      const target = event.target;
      const link = target instanceof Element ? target.closest('a[data-knack-flavour]') : null;
      if (!link || !link.href || this.#prefetched.has(link.href)) return;
      this.#prefetched.add(link.href);
      const tag = document.createElement('link');
      tag.rel = 'prefetch';
      tag.as = 'document';
      tag.href = link.href;
      document.head.appendChild(tag);
    };
    scope.addEventListener('pointerover', prefetch);
    scope.addEventListener('focusin', prefetch);
    scope.addEventListener('touchstart', prefetch, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#scopeEl?.removeEventListener('change', this.#onChange);
    this.#scopeEl?.removeEventListener('click', this.#onBuyOnceClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Move the one-time line + reassurance to just below the native Add to Cart, so the cart button
   * sits directly under the pack cards. The "Buy once" button keeps working from there via the
   * delegated click handler on the section.
   */
  #relocateOnetimeExtras() {
    const extras = this.querySelector('[data-knack-onetime-extras]');
    if (!extras) return;
    const scope = this.#scopeEl ?? document;
    const form = scope.querySelector('product-form-component');
    if (form) form.insertAdjacentElement('afterend', extras);
    extras.removeAttribute('hidden');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-pack-option]')) this.#update('selector');
  };

  /** @returns {HTMLInputElement | null} The checked pack/buy-once radio (scoped to the product section). */
  get #selected() {
    const scope = this.#scopeEl ?? document;
    const el = scope.querySelector('input[data-pack-option]:checked');
    return el instanceof HTMLInputElement ? el : null;
  }

  /** The nearest native product form (buy-buttons) in this product section. */
  get #productForm() {
    const scope = this.closest('.shopify-section') ?? document;
    return scope.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  }

  /** @param {'init' | 'selector'} source */
  #update(source) {
    const sel = this.#selected;
    if (!sel) return;
    const d = sel.dataset;
    const isOnetime = sel.hasAttribute('data-onetime');
    const variantId = d.variantId ?? sel.value;
    const sellingPlanId = isOnetime ? '' : d.sellingPlanId || '';
    const chargedCents = Number(d.chargedCents || 0);

    this.setAttribute('data-purchase', isOnetime ? 'onetime' : 'subscribe');
    this.#steerProductForm(variantId, sellingPlanId);
    if (!isOnetime) this.#syncBuyOnce(sel);
    this.#updateStickyBar();
    this.#announce(d.boxQuantity, isOnetime);

    document.dispatchEvent(
      new CustomEvent('knack:pack:change', {
        bubbles: true,
        composed: true,
        detail: {
          source,
          groupId: this.dataset.groupId,
          productId: d.productId,
          variantId,
          sellingPlanId: sellingPlanId || null,
          purchaseType: isOnetime ? 'onetime' : 'subscribe',
          boxQuantity: d.boxQuantity || '',
          priceCents: chargedCents,
          currency: this.dataset.currency,
        },
      })
    );

    if (source !== 'init') this.#upstackViewContent(variantId, chargedCents, d.boxQuantity);
  }

  /**
   * Point Horizon's native product form at the selected variant + (optionally) the Loop plan.
   * @param {string} variantId @param {string} sellingPlanId
   */
  #steerProductForm(variantId, sellingPlanId) {
    const form = this.#productForm;
    if (!(form instanceof HTMLFormElement)) return;
    const idInput = form.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement) idInput.value = variantId;

    let planInput = form.querySelector('input[name="selling_plan"]');
    if (!(planInput instanceof HTMLInputElement)) {
      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      form.appendChild(planInput);
    }
    if (sellingPlanId) {
      planInput.value = sellingPlanId;
      planInput.disabled = false;
    } else {
      planInput.value = '';
      planInput.disabled = true; // excluded from FormData -> clean one-time line
    }

    // Hidden line-item property mirroring the selling_plan toggle above: marks the line as a
    // subscription for the order-confirmation email. All Loop plans are 4-weekly today.
    let freqInput = form.querySelector('input[name="properties[_delivery_frequency]"]');
    if (!(freqInput instanceof HTMLInputElement)) {
      freqInput = document.createElement('input');
      freqInput.type = 'hidden';
      freqInput.name = 'properties[_delivery_frequency]';
      form.appendChild(freqInput);
    }
    if (sellingPlanId) {
      freqInput.value = '4 weeks';
      freqInput.disabled = false;
    } else {
      freqInput.value = '';
      freqInput.disabled = true;
    }
  }

  /**
   * Make the "Buy once for ..." button track the selected pack: point the buy-once button at the
   * selected pack's product/variant (one-time, no plan) and mirror that pack's one-time price
   * markup into the line. The price markup is pre-rendered server-side (money-filtered, per market)
   * in a <template> on each pack card, so we just clone it (no client-side currency formatting).
   * @param {HTMLInputElement} packRadio The selected pack (subscribe) radio.
   */
  #syncBuyOnce(packRadio) {
    if (!(packRadio instanceof HTMLInputElement) || packRadio.hasAttribute('data-onetime')) return;
    const scope = this.#scopeEl ?? document;
    const buyOnce = scope.querySelector('[data-knack-buyonce]');
    if (!(buyOnce instanceof HTMLElement)) return;

    const variantId = packRadio.dataset.variantId || packRadio.value;
    if (variantId) buyOnce.dataset.variantId = variantId;
    if (packRadio.dataset.onetimeCents) buyOnce.dataset.chargedCents = packRadio.dataset.onetimeCents;
    if (packRadio.dataset.productId) buyOnce.dataset.productId = packRadio.dataset.productId;

    const srcTpl = packRadio.closest('label')?.querySelector('template.knack-pack-selector__onetime-src');
    const srcPrice = srcTpl?.content?.querySelector('.knack-price__current');
    const dest = buyOnce.querySelector('.knack-price__current');
    if (srcPrice && dest) dest.innerHTML = srcPrice.innerHTML;
  }

  /**
   * One-click "Buy once": add the currently selected pack's variant as a one-time line (no selling
   * plan) straight to the cart, then open/refresh the drawer. Mirrors Horizon's native add (requests
   * the cart sections so the drawer morphs in place; dispatches CartAddEvent so the drawer auto-opens).
   * @param {Event} event
   */
  #onBuyOnceClick = async (event) => {
    const target = event.target;
    const btn = target instanceof Element ? target.closest('[data-knack-buyonce]') : null;
    if (!(btn instanceof HTMLElement) || btn.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1'); // one-time: no selling_plan sent

    // Request the cart sections so the drawer/cart re-renders in place, like the native add.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const cfg = fetchConfig('javascript', { body: formData });

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...cfg,
        headers: { ...cfg.headers, Accept: 'text/html' },
      });
      const data = await res.json();

      if (data?.status) {
        document.dispatchEvent(new CartErrorEvent('knack-buyonce', data.message, data.description, data.errors));
        this.#announceText(data.message || 'Could not add to cart.');
        return;
      }

      // Fetch the full cart so the drawer can show the real item count.
      const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => undefined);

      document.dispatchEvent(
        new CartAddEvent(cart ?? undefined, 'knack-buyonce', {
          source: 'knack-buyonce',
          productId: btn.dataset.productId,
          itemCount: 1,
          variantId,
          sections: data.sections,
        })
      );

      this.#announceText('Added one-time, no subscription.');
    } catch (error) {
      console.error('knack buy-once add failed', error);
      this.#announceText('Could not add to cart. Please try again.');
    } finally {
      btn.removeAttribute('aria-busy');
    }
  };

  /** @param {string} text */
  #announceText(text) {
    if (this.refs.status instanceof HTMLElement) this.refs.status.textContent = text;
  }

  /** Mirror the selected option's display price + label into Horizon's native sticky bar. */
  #updateStickyBar() {
    const sel = this.#selected;
    if (!sel) return;
    const label = sel.closest('label');
    const price = label?.querySelector('.knack-price__current');
    const stickyPrice = document.querySelector('.sticky-add-to-cart .sticky-add-to-cart__price');
    if (stickyPrice instanceof HTMLElement && price instanceof HTMLElement) {
      stickyPrice.innerHTML = price.innerHTML;
    }
    const stickyVariant = document.querySelector('.sticky-add-to-cart .sticky-add-to-cart__variant');
    if (stickyVariant instanceof HTMLElement) {
      const box = sel.dataset.boxQuantity || '';
      const mode = sel.hasAttribute('data-onetime') ? ' · One-time' : ' · Subscribe';
      stickyVariant.textContent = `${box}${mode}`;
    }
  }

  /** @param {string} [boxQuantity] @param {boolean} isOnetime */
  #announce(boxQuantity, isOnetime) {
    if (!(this.refs.status instanceof HTMLElement)) return;
    this.refs.status.textContent = `Selected ${boxQuantity || 'pack'}, ${isOnetime ? 'one-time' : 'subscribe and save'}.`;
  }

  /* ---- Upstack (guarded; inert until the app embed is enabled) ---- */
  get #firedVC() {
    window._upstackFiredVC = window._upstackFiredVC || new Set();
    return window._upstackFiredVC;
  }

  /** @param {string} variantId @param {number} cents @param {string} [boxQuantity] */
  #upstackViewContent(variantId, cents, boxQuantity) {
    if (typeof window._upstack !== 'function') return;
    if (this.#firedVC.has(variantId)) return;
    this.#firedVC.add(variantId);
    window._upstack('track', 'view_content', this.#trackingPayload(variantId, cents, boxQuantity));
  }

  /**
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    requestAnimationFrame(() => this.#updateStickyBar());
    // Reset the view_content de-dupe after a real add (not the cart re-render that follows it,
    // which shares the same 'cart:update' event name) so re-selecting a tier re-fires view_content.
    if (event instanceof CartAddEvent) this.#firedVC.clear();
  };

  /** @param {string} variantId @param {number} cents @param {string} [boxQuantity] */
  #trackingPayload(variantId, cents, boxQuantity) {
    const value = cents / 100;
    const currency = this.dataset.currency;
    return {
      value,
      currency,
      items: [
        {
          id: this.dataset.productId,
          variant: variantId,
          name: `${this.dataset.productTitle || ''} - ${boxQuantity || ''}`.trim(),
          sku: variantId,
          price: value,
          currency,
        },
      ],
    };
  }
}

if (!customElements.get('knack-pack-selector')) {
  customElements.define('knack-pack-selector', KnackPackSelector);
}
